/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import { pluck, pluckNumber, getValidValue,
  parseUnsafeString, preDefStr, getDashStyle, mapSymbolName, getFirstColor, BLANKSTRING } from '@fusioncharts/core/src/lib';
import Line from '@fusioncharts/charts/src/dataset/line';
import { _parseToolText as __parseToolText } from './candlestick';

let colorStrings = preDefStr.colors,
  COLOR_B90000 = colorStrings.B90000,
  COLOR_FFFFFF = colorStrings.FFFFFF,
  UNDERSCORE = '_',
  BLANK = '',
  SHOWSHADOW = 'showShadow',
  LINE_STR = 'line',
  ZEROSTRING = '0',
  HUNDRED = '100';

/**
 * Class def for bull stick chart API
 * This dataset is reponsible for drawing stick depending on high and open value
 */
class CandleStickline extends Line {
  /**
    * Function for parsing all the attributes and value given by the user at chart,dataset and set level.
    * This function is called once from the init() function of the Column class.
    * @param  {Object} datasetJSON JSON for dataset configurations
    * @return {boolean} Indicates whether the configuration was successful or not
    */
  configureAttributes (datasetJSON) {
    if (!datasetJSON) {
      return false;
    }
    this.trimData(datasetJSON);
    this.config.JSONData = datasetJSON;

    var dataset = this,
      conf = dataset.config,
      JSONData = dataset.config.JSONData,
      dataArr = JSONData.data || [],
      chartAttr = dataset.getFromEnv('chart-attrib'),
      dataLength = dataArr.length, // eslint-disable-line good-practices/no-single-usage-variable
      numberFormatter = dataset.getFromEnv('number-formatter'),
      colorM = dataset.getFromEnv('color-manager'),
      setColor,
      setBorderColor,
      setAlpha,
      dashStyle,
      // Candle stick properties.
      // Bear fill and border color - (Close lower than open)
      bearBorderColor = conf.bearBorderColor = // eslint-disable-line good-practices/no-single-usage-variable
                    getFirstColor(pluck(chartAttr.bearbordercolor, COLOR_B90000)),
      bearFillColor = conf.bearFillColor = // eslint-disable-line good-practices/no-single-usage-variable
                    getFirstColor(pluck(chartAttr.bearfillcolor, COLOR_B90000)),
      // Bull fill and border color - Close higher than open
      bullBorderColor = conf.bullBorderColor =
                    getFirstColor(pluck(chartAttr.bullbordercolor,
                      colorM.getColor('canvasBorderColor'))),
      bullFillColor = conf.bullFillColor = // eslint-disable-line good-practices/no-single-usage-variable
                getFirstColor(pluck(chartAttr.bullfillcolor,
                  COLOR_FFFFFF)),
      // Line Properties - Serves as line for bar & line and border for candle stick
      plotLineThickness = conf.linethickness = conf.plotBorderThickness = // eslint-disable-line good-practices/no-single-usage-variable
                    pluckNumber(chartAttr.plotlinethickness, 2),
      // Anchor cosmetics in data points
      // Getting anchor cosmetics for the data points or its default values
      // The default value is different from flash in order to render a
      // perfect circle when no anchorside is provided.
      drawAnchors = !!pluckNumber(chartAttr.drawanchors, 0), // eslint-disable-line good-practices/no-single-usage-variable
      setAnchorAngle = pluckNumber(chartAttr.anchorstartangle, 90), // eslint-disable-line good-practices/no-single-usage-variable
      setAnchorRadius = pluckNumber(chartAttr.anchorradius, // eslint-disable-line good-practices/no-single-usage-variable
        this.anchorRadius, 3),
      setAnchorBorderColor = getFirstColor(pluck(chartAttr.anchorbordercolor, // eslint-disable-line good-practices/no-single-usage-variable
        bullBorderColor)),
      setAnchorBorderThickness = pluckNumber(chartAttr.anchorborderthickness, // eslint-disable-line good-practices/no-single-usage-variable
        this.anchorBorderThickness, 1),
      setAnchorBgColor = getFirstColor(pluck(chartAttr.anchorbgcolor, // eslint-disable-line good-practices/no-single-usage-variable
        colorM.getColor('anchorBgColor'))),
      setAnchorAlpha = pluck(chartAttr.anchoralpha, ZEROSTRING),
      setAnchorBgAlpha = pluck(chartAttr.anchorbgalpha, setAnchorAlpha), // eslint-disable-line good-practices/no-single-usage-variable
      pointShadow,
      index,
      dataObj,
      open,
      close,
      high,
      low,
      minValue,
      maxValue,
      x,
      dataStore,
      setData,
      config,
      isCandleStick = false, // eslint-disable-line good-practices/no-single-usage-variable
      plotSpacePercent,
      yMax = -Infinity,
      yMin = +Infinity,
      xMax = -Infinity,
      xMin = +Infinity;
    dataset.setState('visible', pluckNumber(JSONData.visible, 1) === 1);
    conf.plotLineDashLen = pluckNumber(chartAttr.plotlinedashlen, 5);
    conf.plotLineDashGap = pluckNumber(chartAttr.plotlinedashgap, 4);

    dataset._conatinerHidden = !!dataset.getState('visible');
    conf.defaultPadding = {
      left: 0.5,
      right: 0.5
    };
    conf.plotType = LINE_STR;
    conf.parentYAxis = 0;
    conf.toolText = getValidValue(parseUnsafeString(pluck(JSONData.tooltext, chartAttr.plottooltext), false));
    // Dataset seriesname
    conf.name = getValidValue(JSONData.seriesname);
    conf.showTooltip = pluckNumber(chartAttr.showtooltip, 1);

    conf.maxColWidth = Math.abs(pluckNumber(
      pluck(chartAttr.maxcolwidth), 50)) || 1;
    // Animation related attributes configuration
    conf.enableAnimation = pluckNumber(chartAttr.animation,
      chartAttr.defaultanimation, 1);
    conf.animation = !conf.enableAnimation ? false : {
      duration: pluckNumber(chartAttr.animationduration, 1) * 1000
    };
    conf.lineAlpha = pluck(chartAttr.plotlinealpha, HUNDRED);
    plotSpacePercent = Math.max(pluckNumber(chartAttr.plotspacepercent, 20) % 100, 0); // eslint-disable-line good-practices/no-single-usage-variable
    conf.plotSpacePercent = conf.groupPadding = plotSpacePercent / 200;
    dataStore = dataset.components.data = dataset.components.data || (dataset.components.data = []);
    conf.valuePadding = pluckNumber(JSONData.valuepadding, chartAttr.valuepadding, 2);
    conf.plotBorderThickness = plotLineThickness;
    conf.showvalues = 1;
    conf.showShadow = pluckNumber(chartAttr.showshadow, colorM.getColor(SHOWSHADOW));
    // Iterate through all level data
    for (index = 0; index < dataLength; index += 1) {
      // Individual data obj
      // for further manipulation
      setData = dataArr[index];
      dataObj = dataStore[index];
      if (!dataObj) {
        dataObj = dataStore[index] = {};
      }
      !dataObj.config && (dataObj.config = {});
      !dataObj.graphics && (dataObj.graphics = {});
      config = dataObj.config;
      if (setData && !setData.vline) {
        config.setLink = pluck(setData.link);
        open = config.open = numberFormatter.getCleanValue(setData.open);
        close = config.close = numberFormatter.getCleanValue(setData.close);
        high = config.high = numberFormatter.getCleanValue(setData.high);
        low = config.low = numberFormatter.getCleanValue(setData.low);
        config.volume = numberFormatter.getCleanValue(setData.volume, true);

        x = config.x = numberFormatter.getCleanValue(setData.x);
        // openVal = config.openVal = isCandleStick ? Math.abs(close - open) : open;
        config.closeVal = Math.min(open, close);
        config.yVal = Math.max(open, close);
        minValue = Math.min(open, close, high, low);
        maxValue = Math.max(open, close, high, low);

        config.valuePadding = pluckNumber(setData.valuepadding, conf.valuePadding);

        // valueText = parseUnsafeString(getValidValue(setData.valuetext, BLANK));
        setBorderColor = getFirstColor(pluck(setData.bordercolor, close < open ? bearBorderColor
          : bullBorderColor));
        setAlpha = pluck(setData.alpha, conf.lineAlpha);
        setColor = getFirstColor(pluck(setData.color, close < open ? bearFillColor
          : bullFillColor));
        dashStyle = pluckNumber(setData.dashed) ? getDashStyle(conf.plotLineDashLen,
          conf.plotLineDashGap) : 'none';
        config.dashStyle = dashStyle;
        // Set alpha of the shadow
        pointShadow = {
          opacity: conf.showShadow ? (setAlpha / 100) : 0
        };
        config.rawDataColor = setColor;
        config.color = isCandleStick ? setColor : setBorderColor;
        config.alpha = setAlpha;
        config.dashed = pluckNumber(conf.plotLineDashed, setData.dashed, 0);
        /*
         * Storing the set level color and set level alpha which is required in drawing of line
         * In line we are checking setColor and setAlpha of current line segment is same as the previous
         * line segment's setColor and setAlpha by accesing config.setColor and config.setAlpha
         */
        config.setColor = config.color;
        config.setAlpha = config.alpha;
        config.anchorImageUrl = pluck(setData.anchorimageurl, JSONData.anchorimageurl, chartAttr.anchorimageurl);
        // Finally add the data
        // we call getPointStub function that manage displayValue, toolText and link
        config.borderColor = setBorderColor;
        config.borderAlpha = config.plotLineAlpha;
        config.colorArr = [{
          color: config.color,
          alpha: config.alpha
        },
        {
          color: config.borderColor,
          alpha: config.borderAlpha
        }];

        config.dataLabelStyle = dataset._configureDataLabelStyle(setData);

        config.anchorSides = pluckNumber(setData.anchorsides, JSONData.anchorsides,
          chartAttr.anchorsides);
        config.symbol = mapSymbolName(config.anchorSides).split(UNDERSCORE);
        config.anchorProps = {
          enabled: drawAnchors,
          bgColor: setAnchorBgColor,
          symbol: config.symbol,
          bgAlpha: ((setAnchorBgAlpha * setAnchorAlpha) / 100) + BLANK,
          borderColor: setAnchorBorderColor,
          borderAlpha: setAnchorAlpha,
          anchorAlpha: setAnchorAlpha,
          borderThickness: setAnchorBorderThickness,
          imageUrl: config.anchorImageUrl,
          radius: setAnchorRadius,
          imageScale: pluckNumber(setData.imagescale, JSONData.imagescale, chartAttr.imagescale, 100),
          imagePadding: pluckNumber(setData.anchorimagepadding, JSONData.anchorimagepadding,
            chartAttr.anchorimagepadding, 1),
          imageAlpha: pluckNumber(JSONData.anchorimagealpha, chartAttr.anchorimagealpha, 100),
          startAngle: setAnchorAngle
        };
        config.showValue = setData.displayvalue ? 1 : 0;
        config.hoverEffects = {};
        config.anchorSides = pluckNumber(setData.anchorsides, JSONData.anchorsides,
          chartAttr.anchorsides);
        config.symbol = mapSymbolName(config.anchorSides).split(UNDERSCORE);

        config.hoverEffects = {};
        config.y = close;
        config.link = pluck(setData.link);
        config.toolText = dataset._parseToolText(index);
        config.toolTipValue = BLANKSTRING;
        config.setValue = config._y = config.y;
        config.displayValue = parseUnsafeString(pluck(setData.displayvalue, setData.valuetext, BLANK));
        if (minValue !== null) {
          yMax = Math.max(yMax, minValue);
          yMin = Math.min(yMin, minValue);
        }
        if (maxValue !== null) {
          yMax = Math.max(yMax, maxValue);
          yMin = Math.min(yMin, maxValue);
        }
        if (x !== null) {
          xMax = Math.max(xMax, x);
          xMin = Math.min(xMin, x);
        }
        x = x || index + 1;
        config._x = config.x = x;

        config.high = Math.max(open, close, high, low);
        config.low = Math.min(open, close, high, low);
        config.shadow = pointShadow;
      }
    }
    conf.yMax = yMax;
    conf.yMin = yMin;
    conf.xMax = xMax;
    conf.xMin = xMin;
    dataset.setState('dirty', true);
  }
  /**
   * Sets the default value of attributes
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    this.config.drawLine = 1;
  }
  /**
   * Adds dataset Specific event Arguments
   * @param {Object} _dataObj dataObject
   */
  // eslint-disable-next-line class-methods-use-this
  addDatasetSpecificEvtArgs (_dataObj) {
    let dataObj = _dataObj,
      config = dataObj.config,
      eventArgs = dataObj.config.eventArgs;
    Object.assign(eventArgs, {
      open: config.open,
      close: config.close,
      high: config.high,
      low: config.low,
      volume: config.volume,
      alpha: config.alpha,
      x: config.x,
      displayValue: config.displayValue,
      color: config.rawDataColor,
      borderColor: config.borderColor,
      dashed: config.dashed
    });
  }
  /**
   * function to Remove data plots if the number of current data plots/categories
   * is more than the existing ones.
   * @param  {Object} datasetJSON JSON for dataset configurations
   */
  trimData (datasetJSON) {
    if (!this.components && this.components.data && this.components.data.length) {
      return;
    }
    let comps = this.components,
      prevData = comps && comps.data,
      prevDataLength = prevData && prevData.length, // eslint-disable-line good-practices/no-single-usage-variable
      data = datasetJSON.data,
      currDataLength = (Array.isArray(data) && data.filter(v => (v.high || v.open || v.close || v.low)).length) || 0, // eslint-disable-line good-practices/no-function-dependency
      dataDiff = prevDataLength - currDataLength;

    // Removing data plots if the number of current data plots
    // is less than the existing ones.
    if (dataDiff > 0) {
      this.removeData(currDataLength, dataDiff, false);
    }
  }
  /**
   * calculate min and max position to scroll
   */
  calculateScrollRange () {
    let dataset = this,
      conf = dataset.config,
      dataLen = dataset.components.data && dataset.components.data.length; // eslint-disable-line good-practices/no-single-usage-variable
    conf.scrollMinVal = conf.scrollMinValForLabel = 0;
    conf.scrollMaxVal = conf.scrollMaxValForLabel = dataLen;
  }
  /**
   * Function to get the maximum and minimum from dataset
   * @return {Object}     contains max, min value
   */
  getDataLimits () {
    var conf = this.config;
    return {
      max: conf.yMax,
      min: conf.yMin,
      xMax: conf.xMax,
      xMin: conf.xMin
    };
  }
  /**
   * fetches data indices wrt. the x pos value
   * @param {number} x  x pos value
   * @return {Array}    contains indices
   */
  getPlotIndices (x) {
    var dataset = this,
      i,
      minX = Math.floor(x), // eslint-disable-line good-practices/no-single-usage-variable
      maxX = Math.ceil(x), // eslint-disable-line good-practices/no-single-usage-variable
      data,
      returnIndices = [],
      sortedData = dataset.config.JSONData && dataset.config.JSONData.data;

    for (i = sortedData.length; i--;) {
      data = sortedData[i];
      if (data.x >= minX && data.x <= maxX) {
        returnIndices.push(i);
      }
    }
    return returnIndices;
  }
  /**
   * parse tooltext from the dataset json
   * @param {number} i index of data
   * @return {string} tooltext
   */
  _parseToolText (i) {
    return __parseToolText.call(this, i);
  }
  /**
   * Function that return the nearest plot details
   * @param   {number} chartXVal x-axis position of the mouse cordinate
   * @param   {number} chartYVal y-axis position of the mouse cordinate
   * @return {Object} return an object with details of nearest plot and whether it is
   *                   hovered or not
   */
  _getHoveredPlot (chartXVal, chartYVal) {
    var dataset = this,
      chartX = chartXVal,
      chartY = chartYVal,
      xAxis = dataset.getFromEnv('xAxis'),
      dataStore = dataset.components.data,
      pointObj,
      pXs,
      xMin,
      xMax,
      returnValue,
      conf = dataset.config,
      i,
      maxRadius = (conf && conf.radius) || 0;

    // getting new chart XY accordint to Translation of axis
    chartX += xAxis.getTranslation();

    pXs = dataset.getPlotIndices(Math.floor(Math.max(xAxis.getValue(chartX - maxRadius), 0)));
    xMin = pXs[0];
    pXs = dataset.getPlotIndices(Math.ceil(Math.min(xAxis.getValue(chartX + maxRadius), dataStore.length - 1)));
    xMax = pXs[pXs.length - 1];

    [xMin, xMax] = (xMin > xMax) ? [xMax, xMin] : [xMin, xMax];

    for (i = xMax; i >= xMin; i--) {
      pointObj = dataStore[i];
      if (pointObj) {
        returnValue = dataset.isWithinShape(pointObj, i, chartX, chartY);
        if (returnValue) {
          break;
        }
      }
    }

    return returnValue;
  }
  /**
   * Returns the name of the component
   *
   * @return {string} The name of the component
   */
  getName () {
    return 'candlestickline';
  }
}

export default CandleStickline;
